import React, { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { JellyfinItem } from "../api/jellyfin";
import { colors, radius, spacing, systemSansFont, typography } from "../theme/tokens";
import { FocusablePressable } from "./FocusablePressable";
import { getFocusModality } from "../tvNavigation/registry";
import { isTelevisionRuntime } from "../utils/platformCapabilities";

type Props = { items: JellyfinItem[]; onPress: (item: JellyfinItem) => void };
const palette = ["#7842ad", "#a83d95", "#4eaf6f", "#ad5147", "#3975a9", "#9b6f31"];
const CARD_WIDTH = 200;
const CARD_WIDTH_COMPACT = 156;

function GenreCard({ item, color, width, onPress, onFocusChange }: { item: JellyfinItem; color: string; width: number; onPress: () => void; onFocusChange: (focused: boolean) => void }) {
  const progress = useRef(new Animated.Value(0)).current;
  const animate = (active: boolean) => {
    progress.stopAnimation();
    Animated.timing(progress, {
      toValue: active ? 1 : 0,
      duration: active ? 180 : 140,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  };
  return (
    <Animated.View style={{ transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [0, -3] }) }, { scale: progress.interpolate({ inputRange: [0, 1], outputRange: [1, 1.035] }) }] }}>
      <FocusablePressable
        testID={`genre-card-${item.Id ?? item.Name ?? "unknown"}`}
        accessibilityLabel={`Open genre ${item.Name ?? "Genre"}`}
        onPress={onPress}
        onHoverIn={() => animate(true)}
        onHoverOut={() => animate(false)}
        onFocus={() => { animate(true); onFocusChange(true); }}
        onBlur={() => { animate(false); onFocusChange(false); }}
        style={(focused, pressed) => [styles.card, { width, backgroundColor: color }, focused && styles.focused, pressed && styles.pressed]}
      >
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFillObject, styles.cardHighlight, { opacity: progress }]} />
        <Text numberOfLines={2} style={styles.label}>{item.Name ?? "Genre"}</Text>
      </FocusablePressable>
    </Animated.View>
  );
}

export function GenreShelf({ items, onPress }: Props) {
  const { width } = useReferenceWindowDimensions();
  const television = isTelevisionRuntime();
  const cardWidth = television ? 250 : CARD_WIDTH;
  const pagePadding = television ? 64 : width >= 1280 ? 56 : width > 760 ? 32 : 16;
  const viewportWidth = Math.max(1, width - pagePadding * 2);
  const listRef = useRef<FlatList<JellyfinItem>>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (exitTimer.current) clearTimeout(exitTimer.current); }, []);
  const setControlsActive = (active: boolean) => {
    if (exitTimer.current) clearTimeout(exitTimer.current);
    if (active) setControlsVisible(true);
    else exitTimer.current = setTimeout(() => setControlsVisible(false), 120);
  };
  const handleFocusChange = (active: boolean) => {
    if (getFocusModality() === 'gamepad' || getFocusModality() === 'native-tv') {
      if (exitTimer.current) clearTimeout(exitTimer.current);
      setControlsVisible(false);
    } else setControlsActive(active);
  };
  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const scroll = (direction: -1 | 1) => listRef.current?.scrollToOffset({ offset: Math.max(0, Math.min(maxOffset, offset + direction * viewportWidth * 0.82)), animated: true });
  const updateOffset = (event: NativeSyntheticEvent<NativeScrollEvent>) => setOffset(event.nativeEvent.contentOffset.x);
  if (!items.length) return null;
  return (
    <View style={styles.root}>
      <Text style={[typography.sectionTitle, television ? styles.titleTelevision : width <= 760 ? styles.titleMobile : styles.titleDesktop, { marginHorizontal: pagePadding }]}>Genres</Text>
      <Pressable accessible={false} focusable={false} onHoverIn={() => setControlsActive(true)} onHoverOut={() => setControlsActive(false)} style={styles.listShell}>
        <FlatList
          ref={listRef}
          horizontal
          data={items}
          keyExtractor={(item, index) => item.Id ?? `genre-${index}`}
          showsHorizontalScrollIndicator={false}
          onContentSizeChange={(nextWidth) => setContentWidth(Math.max(0, nextWidth - pagePadding * 2))}
          onScroll={updateOffset}
          scrollEventThrottle={64}
          contentContainerStyle={{ paddingHorizontal: pagePadding, paddingTop: spacing.lg, paddingBottom: spacing.lg }}
          renderItem={({ item, index }) => <GenreCard item={item} width={television ? 250 : width <= 760 ? CARD_WIDTH_COMPACT : CARD_WIDTH} color={palette[index % palette.length]} onPress={() => onPress(item)} onFocusChange={handleFocusChange} />}
        />
        {width > 760 && controlsVisible && offset > 8 ? <FocusablePressable controllerFocusable={false} accessibilityLabel="Scroll Genres left" onPress={() => scroll(-1)} onHoverIn={() => setControlsActive(true)} style={[styles.edgeButton, styles.edgeLeft]}><ChevronLeft color="#fff" size={24} /></FocusablePressable> : null}
        {width > 760 && controlsVisible && offset < maxOffset - 8 ? <FocusablePressable controllerFocusable={false} accessibilityLabel="Scroll Genres right" onPress={() => scroll(1)} onHoverIn={() => setControlsActive(true)} style={[styles.edgeButton, styles.edgeRight]}><ChevronRight color="#fff" size={24} /></FocusablePressable> : null}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { marginBottom: 50, paddingTop: 5 },
  listShell: { position: "relative" },
  cardCompact: { width: CARD_WIDTH_COMPACT, height: 88 },
  card: { width: CARD_WIDTH, height: 112, marginRight: 10, paddingHorizontal: 14, paddingBottom: 12, justifyContent: "flex-end", overflow: "hidden", borderRadius: radius.medium, borderWidth: 2, borderColor: "transparent" },
  cardHighlight: { borderRadius: radius.medium, backgroundColor: "rgba(255,255,255,0.11)" },
  focused: { borderColor: colors.text },
  pressed: { opacity: 0.8 },
  label: { color: colors.text, fontFamily: systemSansFont, fontSize: 15, fontWeight: "700", textShadowColor: "rgba(0,0,0,0.8)", textShadowRadius: 4 },
  titleDesktop: { fontSize: 24, lineHeight: 29, fontWeight: "700" },
  titleMobile: { fontSize: 18, lineHeight: 23, fontWeight: "700" },
  titleTelevision: { fontSize: 30, lineHeight: 38, fontWeight: "700" },
  edgeButton: { position: "absolute", top: spacing.lg, bottom: spacing.lg, width: 46, zIndex: 20, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(12,12,12,0.82)" },
  edgeLeft: { left: 0, borderTopRightRadius: 8, borderBottomRightRadius: 8 },
  edgeRight: { right: 0, borderTopLeftRadius: 8, borderBottomLeftRadius: 8 },
});
